import { Switch } from '@headlessui/react';
import { useEffect, useState } from 'react';

const styles = {
    simple: {
        span: `pointer-events-none inline-block h-5 w-5 rounded-full bg-white dark:bg-white/[0.12] shadow transform 
               ring-0 transition ease-in-out duration-200`,

        base: `relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full 
               cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 
               focus:ring-offset-2 focus:ring-indigo-500`,
    },
    short: {
        base: `flex-shrink-0 group relative rounded-full inline-flex items-center justify-center h-5 w-10 
               cursor-pointer focus:outline-none`,
        background: `pointer-events-none absolute h-4 w-9 mx-auto rounded-full transition-colors ease-in-out duration-200`,
        circle: `pointer-events-none absolute left-0 inline-block h-5 w-5 border border-gray-200 rounded-full bg-white shadow 
                 transform ring-0 transition-transform ease-in-out duration-200`,
    },
};

export type ToggleProps = {
    onToggle: (enabled: boolean) => void;
    type?: 'short' | 'simple';
    defaultEnabled?: boolean;
    label?: string;
    subLabel?: string;
    isDisabled?: boolean;
};

function Toggle({ onToggle, label, subLabel, defaultEnabled = false, type = 'simple', isDisabled = false }: ToggleProps): JSX.Element {
    const [_enabled, setEnabled] = useState(defaultEnabled);

    function toggle(enabled: boolean): void {
        setEnabled(enabled);
        onToggle(enabled);
    }

    useEffect(() => {
        setEnabled(defaultEnabled);
    }, [defaultEnabled]);

    if (type === 'short') {
        return (
            <Switch
                checked={_enabled}
                disabled={isDisabled}
                onChange={toggle}
                className={`${styles.short.base} ${isDisabled ? 'opacity-50 cursor-default' : ''}`}
            >
                <span className="sr-only">Use setting</span>
                <span aria-hidden="true" className="pointer-events-none absolute w-full h-full rounded-md " />
                <span
                    aria-hidden="true"
                    className={`${_enabled ? 'bg-indigo-600' : 'bg-gray-200 dark:bg-white/[0.12]'} ${styles.short.background}`}
                />
                <span aria-hidden="true" className={`${_enabled ? 'translate-x-5' : 'translate-x-0'} ${styles.short.circle}`} />
            </Switch>
        );
    }
    return (
        <Switch.Group as="div" className="flex items-center">
            <Switch
                checked={_enabled}
                disabled={isDisabled}
                onChange={toggle}
                className={`${styles.simple.base} ${_enabled ? 'bg-indigo-600' : 'bg-gray-200'} ${
                    isDisabled ? 'opacity-50 cursor-default' : ''
                }`}
            >
                <span aria-hidden="true" className={`${styles.simple.span} ${_enabled ? 'translate-x-5' : 'translate-x-0'}`} />
            </Switch>
            <Switch.Label as="span" className="ml-3">
                {label && <span className="text-sm font-medium text-gray-900">{label} </span>}
                {subLabel && <span className="text-sm text-gray-500">{subLabel}</span>}
            </Switch.Label>
        </Switch.Group>
    );
}

export default Toggle;
